import { useNavigate, useRouterState } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';

import { ChatSection } from '@/components/blocks/Layout/Sidebar/ChatSection';
import { YStack } from '@/components/ui/layout/layout';
import { SidePanel } from '@/design-system-v2/side-panel';
import { SidebarRow } from '@/design-system-v2/sidebar-row';
import { AgentsSidebar } from '@/features/agents/agents-sidebar';
import {
  MeetingsSettingsButton,
  MeetingsTimelineSidebar,
} from '@/features/meetings/meetings-timeline-sidebar';
import { PluginsSidebar } from '@/features/plugins/plugins-sidebar';
import { RoutineChatSidebarSection } from '@/features/routines/components/routine-chat-sidebar-section';
import { SettingsSidebar } from '@/features/settings/settings-sidebar';
import {
  SpritesSearchButton,
  SpritesSidebar,
  SpritesViewToggle,
} from '@/features/sprites/sprites-sidebar';
import { Routes } from '@/types/routes';

import { NavRailFooterCards } from './nav-rail-footer';

import type { NavRailPanel as NavRailPanelKind } from './nav-rail.utils';

const PANEL_TITLES: Record<NavRailPanelKind, string> = {
  chats: 'Chats',
  agents: 'Agents',
  settings: 'Settings',
  library: 'Library',
  integrations: 'Integrations',
  meetings: 'Meetings',
};

export const NavRailPanel = observer(
  ({ panel }: { panel: NavRailPanelKind }) => {
    return (
      <SidePanel.Root>
        <SidePanel.Header>
          <SidePanel.Title>{PANEL_TITLES[panel]}</SidePanel.Title>
          {panel === 'meetings' && (
            <SidePanel.Actions>
              <MeetingsSettingsButton />
            </SidePanel.Actions>
          )}
          {panel === 'library' && (
            <SidePanel.Actions>
              <SpritesSearchButton />
              <SpritesViewToggle />
            </SidePanel.Actions>
          )}
        </SidePanel.Header>
        {panel === 'chats' && <ChatsPanelBody />}
        {panel === 'agents' && <AgentsSidebar />}
        {panel === 'settings' && <SettingsSidebar />}
        {panel === 'library' && <SpritesSidebar />}
        {panel === 'integrations' && <PluginsSidebar />}
        {panel === 'meetings' && <MeetingsTimelineSidebar />}
      </SidePanel.Root>
    );
  }
);

const NewChatItem = () => {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isNewChat = pathname === Routes.Chats || pathname === '/';

  return (
    <SidePanel.PrimaryAction>
      <SidebarRow.Button
        leading={<SidebarRow.AddIcon />}
        selected={isNewChat}
        onClick={() =>
          void navigate({ to: Routes.Chats as string, search: { new: true } })
        }
      >
        New chat
      </SidebarRow.Button>
    </SidePanel.PrimaryAction>
  );
};

const ChatsPanelBody = observer(() => {
  return (
    <div className="contents">
      <NewChatItem />
      <YStack className="min-h-0 flex-1">
        <ChatSection afterProjectsContent={<RoutineChatSidebarSection />} />
      </YStack>
      <YStack className="mt-2 min-w-40 gap-2 pb-4 empty:hidden">
        <NavRailFooterCards />
      </YStack>
    </div>
  );
});
